import { useCallback, useMemo, useState } from 'react';
import { deleteJob, downloadVideo, fetchThumbnailBlobUrl, rerunJob } from '../api';
import { useBlobUrl } from '../hooks/useBlobUrl';
import { calculateVideoJobCost } from '../hooks/useVideoStats';
import type { Job, JobStatus } from '../types';
import { jobEndpointCosts } from '../utils/falPricing';
import { PlayerModal, type PlayerMode } from './PlayerModal';
import { RunConfigModal } from './RunConfigModal';

/**
 * Format a per-scan FAL cost. Tiered precision matches lightning's
 * `formatCost` helper so the History table reads consistently across
 * apps for sub-cent / sub-dollar / multi-dollar values.
 */
function formatCost(amount: number): string {
  if (amount === 0) return '—';
  if (amount < 0.01) return `$${amount.toFixed(4)}`;
  if (amount < 1) return `$${amount.toFixed(3)}`;
  return `$${amount.toFixed(2)}`;
}

interface HistoryTableProps {
  jobs: Job[];
  onMutated?: () => void;
}

// Compact two-line date-time formatter so the "Completed At" column
// stays narrow without truncating the day. Renders as "Apr 28, 2026"
// over "10:42 AM" in the table body; falls back to the full locale
// string in the cell title for hover detail.
const COMPACT_DATE_FMT = new Intl.DateTimeFormat(undefined, {
  month: 'short',
  day: 'numeric',
  year: 'numeric',
});
const COMPACT_TIME_FMT = new Intl.DateTimeFormat(undefined, {
  hour: 'numeric',
  minute: '2-digit',
});

function formatCompactDateTime(value: string | null): {
  date: string;
  time: string;
  full: string;
} {
  if (!value) return { date: '—', time: '', full: '' };
  const d = new Date(value);
  return {
    date: COMPACT_DATE_FMT.format(d),
    time: COMPACT_TIME_FMT.format(d),
    full: d.toLocaleString(),
  };
}

function formatDuration(seconds: number | null): string {
  if (seconds == null) return '—';
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? `${m}m ${String(s).padStart(2, '0')}s` : `${s}s`;
}

function jobModel(job: Job): string {
  return job.config_used?.model ?? '—';
}

function StatusPill({ status }: { status: Job['status'] }) {
  const styles =
    status === 'completed'
      ? 'bg-green-100 text-green-800'
      : status === 'failed'
      ? 'bg-red-100 text-red-800'
      : 'bg-primary-50 text-primary-600';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-xs font-medium ${styles}`}>
      <span
        className={`w-1.5 h-1.5 rounded-full ${
          status === 'completed' ? 'bg-green-500' : status === 'failed' ? 'bg-red-500' : 'bg-primary-500'
        }`}
      />
      {status}
    </span>
  );
}

interface ThumbnailProps {
  job: Job;
  onClick?: () => void;
}

function Thumbnail({ job, onClick }: ThumbnailProps) {
  const [errored, setErrored] = useState(false);

  const fetcher = useCallback(
    (signal: AbortSignal) => fetchThumbnailBlobUrl(job.job_id, signal),
    [job.job_id],
  );
  const thumbSrc = useBlobUrl(job.status === 'completed' ? fetcher : null);

  const showImage = job.status === 'completed' && !errored && thumbSrc;
  const baseClass =
    'h-8 w-14 rounded-md border border-primary-100 bg-primary-50 flex items-center justify-center overflow-hidden shrink-0';
  if (!showImage) {
    return (
      <div className={baseClass} aria-hidden>
        <svg className="w-4 h-4 text-primary-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      </div>
    );
  }
  const img = (
    <img
      src={thumbSrc}
      alt=""
      decoding="async"
      onError={() => setErrored(true)}
      className="w-full h-full object-cover"
    />
  );
  if (!onClick) {
    return <div className={baseClass}>{img}</div>;
  }
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${baseClass} hover:ring-2 hover:ring-primary-300 transition-shadow cursor-pointer`}
      title="Play video"
      aria-label="Play video"
    >
      {img}
    </button>
  );
}

function UVCampLink({ link }: { link: string | null }) {
  if (!link) return <span className="text-primary-300">—</span>;
  return (
    <a
      href={link}
      target="_blank"
      rel="noopener noreferrer"
      className="text-primary-600 hover:text-primary-700 hover:underline font-medium text-xs"
      title={link}
    >
      Open
      <svg className="w-3 h-3 inline-block ml-0.5 -mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14"
        />
      </svg>
    </a>
  );
}

interface ActionsCellProps {
  job: Job;
  busyId: string | null;
  onView: (job: Job, mode: PlayerMode) => void;
  onShowConfig: (job: Job) => void;
  onRerun: (job: Job) => void;
  onDelete: (job: Job) => void;
  onDownload: (job: Job) => void;
}

function ActionsCell({ job, busyId, onView, onShowConfig, onRerun, onDelete, onDownload }: ActionsCellProps) {
  const busy = busyId === job.job_id;
  const baseBtn =
    'px-2 py-0.5 rounded-md text-[11px] font-medium border transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap';

  return (
    <div className="inline-flex items-center gap-1 flex-nowrap justify-end">
      {job.status === 'completed' && (
        <>
          <button
            type="button"
            onClick={() => onView(job, 'video')}
            disabled={busy}
            className={`${baseBtn} border-primary-200 text-primary-700 bg-white hover:bg-primary-50`}
            title="Play the rendered video"
          >
            Play
          </button>
          <button
            type="button"
            onClick={() => onView(job, '360')}
            disabled={busy}
            className={`${baseBtn} border-primary-200 text-primary-700 bg-white hover:bg-primary-50`}
            title="Drag-scrub through the rendered MP4 as a 360° rotation"
          >
            360°
          </button>
          <button
            type="button"
            onClick={() => onDownload(job)}
            disabled={busy}
            className={`${baseBtn} border-transparent text-white bg-brand-gradient hover:opacity-95 shadow-card`}
            title="Download the rendered MP4"
          >
            Download
          </button>
        </>
      )}
      <button
        type="button"
        onClick={() => onShowConfig(job)}
        disabled={busy || !job.config_used}
        className={`${baseBtn} border-primary-200 text-primary-700 bg-white hover:bg-primary-50`}
        title={job.config_used ? 'Show the config used for this run' : 'No config snapshot for this job'}
      >
        Config
      </button>
      <button
        type="button"
        onClick={() => onRerun(job)}
        disabled={busy}
        className={`${baseBtn} border-primary-200 text-primary-700 bg-white hover:bg-primary-50`}
        title="Submit a new job with the same config"
      >
        {busy ? '…' : 'Rerun'}
      </button>
      <button
        type="button"
        onClick={() => onDelete(job)}
        disabled={busy}
        className={`${baseBtn} border-red-200 text-red-700 bg-white hover:bg-red-50`}
        title="Delete this job and its rendered files"
      >
        Delete
      </button>
    </div>
  );
}

// ---------- CSV export ----------

const CSV_COLUMNS: Array<{ key: string; header: string; getValue: (j: Job) => unknown }> = [
  { key: 'job_id', header: 'job_id', getValue: (j) => j.job_id },
  { key: 'scan_id', header: 'scan_id', getValue: (j) => j.scan_id },
  { key: 'status', header: 'status', getValue: (j) => j.status },
  { key: 'model', header: 'model', getValue: (j) => j.config_used?.model ?? '' },
  { key: 'created_at', header: 'created_at', getValue: (j) => j.created_at },
  { key: 'started_at', header: 'started_at', getValue: (j) => j.started_at ?? '' },
  { key: 'completed_at', header: 'completed_at', getValue: (j) => j.completed_at ?? '' },
  { key: 'duration_seconds', header: 'duration_seconds', getValue: (j) => j.duration_seconds ?? '' },
  { key: 'organization_id', header: 'organization_id', getValue: (j) => j.organization_id ?? '' },
  { key: 'site_id', header: 'site_id', getValue: (j) => j.site_id ?? '' },
  { key: 'uvcamp_link', header: 'uvcamp_link', getValue: (j) => j.uvcamp_link ?? '' },
  { key: 'video_path', header: 'video_path', getValue: (j) => j.video_path ?? '' },
  { key: 'error', header: 'error', getValue: (j) => j.error ?? '' },
  { key: 'cfg_prompt', header: 'cfg_prompt', getValue: (j) => j.config_used?.prompt ?? '' },
  { key: 'cfg_negative_prompt', header: 'cfg_negative_prompt', getValue: (j) => j.config_used?.negative_prompt ?? '' },
  { key: 'cfg_duration', header: 'cfg_duration', getValue: (j) => j.config_used?.duration ?? '' },
  { key: 'cfg_resolution', header: 'cfg_resolution', getValue: (j) => j.config_used?.resolution ?? '' },
  { key: 'cfg_aspect_ratio', header: 'cfg_aspect_ratio', getValue: (j) => j.config_used?.aspect_ratio ?? '' },
  { key: 'cfg_seed', header: 'cfg_seed', getValue: (j) => j.config_used?.seed ?? '' },
  { key: 'cfg_generate_audio', header: 'cfg_generate_audio', getValue: (j) => j.config_used?.generate_audio ?? '' },
  { key: 'cfg_auto_fix', header: 'cfg_auto_fix', getValue: (j) => j.config_used?.auto_fix ?? '' },
  { key: 'fal_total_cost', header: 'fal_total_cost', getValue: (j) => calculateVideoJobCost(j) },
  { key: 'fal_usage_json', header: 'fal_usage_json', getValue: (j) => (j.fal_usage ? JSON.stringify(j.fal_usage) : '') },
];

function csvEscape(value: unknown): string {
  if (value === null || value === undefined) return '';
  const s = String(value);
  if (/[",\r\n]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function buildCsv(jobs: Job[]): string {
  const header = CSV_COLUMNS.map((c) => c.header).join(',');
  const lines = jobs.map((job) => CSV_COLUMNS.map((c) => csvEscape(c.getValue(job))).join(','));
  return [header, ...lines].join('\r\n');
}

function downloadCsv(filename: string, content: string): void {
  const blob = new Blob(['\ufeff', content], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}

// ---------- Filter bar + table ----------

type StatusFilter = 'all' | JobStatus;

interface FilterBarProps {
  query: string;
  onQuery: (v: string) => void;
  statusFilter: StatusFilter;
  onStatusFilter: (v: StatusFilter) => void;
  modelFilter: string;
  onModelFilter: (v: string) => void;
  modelOptions: string[];
  totalCount: number;
  visibleCount: number;
  onExport: () => void;
  onClear: () => void;
}

function FilterBar({
  query,
  onQuery,
  statusFilter,
  onStatusFilter,
  modelFilter,
  onModelFilter,
  modelOptions,
  totalCount,
  visibleCount,
  onExport,
  onClear,
}: FilterBarProps) {
  const inputClass =
    'px-3 py-1.5 rounded-lg border border-primary-200 bg-white focus:border-primary-500 focus:ring-2 focus:ring-primary-200 outline-none text-sm';
  const filtered = visibleCount !== totalCount;
  return (
    <div className="rounded-2xl border border-primary-100 bg-white shadow-card p-3 flex flex-wrap items-center gap-3">
      <div className="flex-1 min-w-[14rem]">
        <input
          type="search"
          value={query}
          onChange={(e) => onQuery(e.target.value)}
          placeholder="Search scan ID, organization, site, error…"
          className={`${inputClass} w-full`}
        />
      </div>
      <select
        value={statusFilter}
        onChange={(e) => onStatusFilter(e.target.value as StatusFilter)}
        className={inputClass}
        title="Filter by status"
      >
        <option value="all">All statuses</option>
        <option value="completed">Completed</option>
        <option value="failed">Failed</option>
      </select>
      <select
        value={modelFilter}
        onChange={(e) => onModelFilter(e.target.value)}
        className={inputClass}
        title="Filter by model"
      >
        <option value="all">All models</option>
        {modelOptions.map((m) => (
          <option key={m} value={m}>
            {m}
          </option>
        ))}
      </select>
      <button
        type="button"
        onClick={onClear}
        disabled={!filtered && !query}
        className="px-3 py-1.5 rounded-lg text-sm text-primary-700 hover:bg-primary-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
      >
        Clear
      </button>
      <div className="flex-1" />
      <div className="text-xs text-primary-500 whitespace-nowrap">
        Showing <span className="font-semibold text-primary-900">{visibleCount}</span> of{' '}
        <span className="font-semibold text-primary-900">{totalCount}</span>
      </div>
      <button
        type="button"
        onClick={onExport}
        disabled={visibleCount === 0}
        className="px-3 py-1.5 rounded-lg text-sm font-medium text-white bg-brand-gradient shadow-card hover:shadow-card-hover hover:opacity-95 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
        title={
          visibleCount === 0
            ? 'No rows to export'
            : `Export the ${visibleCount} currently visible row(s) to CSV`
        }
      >
        Export CSV
      </button>
    </div>
  );
}

export function HistoryTable({ jobs, onMutated }: HistoryTableProps) {
  const [player, setPlayer] = useState<{ job: Job; mode: PlayerMode } | null>(null);
  const [openConfig, setOpenConfig] = useState<Job | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');
  const [modelFilter, setModelFilter] = useState<string>('all');
  // Mobile cards collapse to Scan ID / Status / Cost by default; tapping
  // a card expands it. Keyed by job_id so expansion survives re-renders.
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());

  const toggleRow = useCallback((jobId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(jobId)) next.delete(jobId);
      else next.add(jobId);
      return next;
    });
  }, []);

  const modelOptions = useMemo(() => {
    const set = new Set<string>();
    for (const j of jobs) {
      const m = j.config_used?.model;
      if (m) set.add(m);
    }
    return Array.from(set).sort();
  }, [jobs]);

  const filteredJobs = useMemo(() => {
    const q = query.trim().toLowerCase();
    return jobs.filter((j) => {
      if (statusFilter !== 'all' && j.status !== statusFilter) return false;
      if (modelFilter !== 'all' && jobModel(j) !== modelFilter) return false;
      if (q) {
        const hay = [
          j.scan_id,
          j.job_id,
          j.organization_id ?? '',
          j.site_id ?? '',
          j.error ?? '',
          jobModel(j),
        ]
          .join(' ')
          .toLowerCase();
        if (!hay.includes(q)) return false;
      }
      return true;
    });
  }, [jobs, query, statusFilter, modelFilter]);

  const handleRerun = async (job: Job) => {
    setBusyId(job.job_id);
    setError(null);
    try {
      await rerunJob(job.job_id);
      onMutated?.();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (job: Job) => {
    if (!window.confirm(`Delete job for scan ${job.scan_id}? This will also remove the saved MP4.`)) {
      return;
    }
    setBusyId(job.job_id);
    setError(null);
    try {
      await deleteJob(job.job_id);
      onMutated?.();
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusyId(null);
    }
  };

  const handleDownload = async (job: Job) => {
    setBusyId(job.job_id);
    setError(null);
    try {
      await downloadVideo(job.job_id, job.scan_id);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : String(ex));
    } finally {
      setBusyId(null);
    }
  };

  const handleExport = () => {
    const ts = new Date().toISOString().replace(/[:.]/g, '-');
    downloadCsv(`360_video_history_${ts}.csv`, buildCsv(filteredJobs));
  };

  const handleClearFilters = () => {
    setQuery('');
    setStatusFilter('all');
    setModelFilter('all');
  };

  return (
    <div className="space-y-3">
      <FilterBar
        query={query}
        onQuery={setQuery}
        statusFilter={statusFilter}
        onStatusFilter={setStatusFilter}
        modelFilter={modelFilter}
        onModelFilter={setModelFilter}
        modelOptions={modelOptions}
        totalCount={jobs.length}
        visibleCount={filteredJobs.length}
        onExport={handleExport}
        onClear={handleClearFilters}
      />

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>
      )}

      {jobs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-primary-200 bg-white px-6 py-10 text-center text-primary-500 text-sm">
          No completed scans yet.
        </div>
      ) : filteredJobs.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-primary-200 bg-white px-6 py-10 text-center text-primary-500 text-sm">
          No rows match the current filters.
        </div>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-primary-100 bg-white shadow-card">
          {/* Compact layout: every body cell uses `whitespace-nowrap`
              so rows stay one line tall. The parent
              `overflow-x-auto` handles long content (e.g. the action
              row, or unusually wide IDs) by horizontally scrolling
              instead of vertically wrapping. The Error column is the
              only deliberately wrapped cell — error messages are
              rare but worth reading in full when present. */}
          <table className="data-table mobile-collapsible min-w-full text-sm">
            <thead className="bg-primary-50/60 text-primary-600 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="pl-3 pr-2 py-2 text-left font-semibold w-16"></th>
                <th className="px-3 py-2 text-left font-semibold">Scan ID</th>
                <th className="px-3 py-2 text-left font-semibold">UVCamp</th>
                <th className="px-3 py-2 text-left font-semibold">Status</th>
                <th className="px-3 py-2 text-left font-semibold">Model</th>
                <th className="px-3 py-2 text-left font-semibold">Completed</th>
                <th className="px-3 py-2 text-left font-semibold">Duration</th>
                <th className="px-3 py-2 text-left font-semibold">Cost</th>
                <th className="px-3 py-2 text-left font-semibold">Error</th>
                <th className="px-3 py-2 text-right font-semibold">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-primary-100">
              {filteredJobs.map((job) => {
                const ts = formatCompactDateTime(job.completed_at);
                return (
                <tr
                  key={job.job_id}
                  className="hover:bg-primary-50/40 align-middle"
                  data-collapsed={!expandedRows.has(job.job_id)}
                  onClick={(e) => {
                    if ((e.target as HTMLElement).closest('button, a, input, select')) return;
                    toggleRow(job.job_id);
                  }}
                >
                  <td className="pl-3 pr-2 py-2" data-label="Preview">
                    <Thumbnail
                      job={job}
                      onClick={
                        job.status === 'completed'
                          ? () => setPlayer({ job, mode: 'video' })
                          : undefined
                      }
                    />
                  </td>
                  <td
                    className="px-3 py-2 font-mono text-[11px] text-primary-900 whitespace-nowrap"
                    title={job.scan_id}
                    data-label="Scan ID"
                  >
                    {job.scan_id}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="UVCamp"><UVCampLink link={job.uvcamp_link} /></td>
                  <td className="px-3 py-2 whitespace-nowrap" data-label="Status"><StatusPill status={job.status} /></td>
                  <td className="px-3 py-2 text-[11px] font-mono text-primary-700 whitespace-nowrap" data-label="Model">{jobModel(job)}</td>
                  <td
                    className="px-3 py-2 text-[11px] text-primary-700 whitespace-nowrap leading-tight"
                    title={ts.full}
                    data-label="Completed"
                  >
                    <div>{ts.date}</div>
                    {ts.time && <div className="text-primary-500">{ts.time}</div>}
                  </td>
                  <td className="px-3 py-2 text-primary-700 font-mono text-xs whitespace-nowrap" data-label="Duration">
                    {formatDuration(job.duration_seconds)}
                  </td>
                  <td
                    className="px-3 py-2 text-primary-700 font-mono text-xs whitespace-nowrap"
                    data-label="Cost"
                    title={(() => {
                      if (!job.fal_usage) return 'No FAL usage data recorded for this job';
                      const breakdown = jobEndpointCosts(job);
                      const callsByEp = job.fal_usage;
                      return breakdown
                        .map((ec) => {
                          const calls = callsByEp[ec.endpoint]?.calls ?? 0;
                          const tag =
                            ec.source === 'billed'
                              ? '(billed)'
                              : ec.source === 'estimated'
                              ? '(estimated)'
                              : '(estimate unavailable)';
                          return `${ec.endpoint}: $${ec.cost.toFixed(4)} ${tag} — ${calls} calls`;
                        })
                        .join('\n');
                    })()}
                  >
                    {(() => {
                      const cost = calculateVideoJobCost(job);
                      return cost > 0 ? (
                        formatCost(cost)
                      ) : (
                        <span className="text-primary-300">—</span>
                      );
                    })()}
                  </td>
                  <td
                    className="px-3 py-2 text-[11px] text-red-700 max-w-[14rem] truncate"
                    title={job.error ?? undefined}
                    data-label="Error"
                  >
                    {job.error || <span className="text-primary-300">—</span>}
                  </td>
                  <td className="px-3 py-2 text-right whitespace-nowrap" data-label="Actions">
                    <ActionsCell
                      job={job}
                      busyId={busyId}
                      onView={(j, mode) => setPlayer({ job: j, mode })}
                      onShowConfig={setOpenConfig}
                      onRerun={handleRerun}
                      onDelete={handleDelete}
                      onDownload={handleDownload}
                    />
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <PlayerModal
        job={player?.job ?? null}
        initialMode={player?.mode ?? 'video'}
        onClose={() => setPlayer(null)}
      />
      <RunConfigModal job={openConfig} onClose={() => setOpenConfig(null)} />
    </div>
  );
}
